/**
 * Task due dates: which calendar day a task is due, and the datetime-local
 * form round trip.
 *
 * membership_actions.due_date is timestamptz and holds TWO shapes (measured on
 * gymnastics-of-york 2026-09-14): date-only values ("YYYY-MM-DD" from the
 * follow-up prompt and scheduled cancellations, stored as 00:00:00 UTC) and
 * real instants (the task forms). Taking the date part reads evening instants
 * a day late; converting everything reads date-only values a day early. So a
 * bare date or exactly-midnight-UTC value is date-only, and anything else is
 * an instant read in local time.
 *
 * The backend (src/utils/taskDueDay.js) and mobile (features/tasks/hooks.ts)
 * apply the same rule, so My Tasks, the membership Actions tab, the dashboard
 * widget, the digest email and the phone agree on what is overdue.
 */

import { localTodayYmd } from "@mediatechgroup/calendar-date";

const DATE_ONLY = /^(\d{4}-\d{2}-\d{2})(?:[T ]00:00(?::00(?:\.0+)?)?(?:Z|[+-]00(?::?00)?)?)?$/;

const pad = (n: number) => String(n).padStart(2, "0");

/** True for a bare date or a midnight-UTC timestamp, the date-only shape. */
export function isDateOnlyValue(value: string | null | undefined): boolean {
  if (!value) return false;
  return DATE_ONLY.test(String(value).trim());
}

/** The local calendar day (`YYYY-MM-DD`) a task is due, or null. */
export function taskDueDay(value: string | null | undefined): string | null {
  if (!value) return null;
  const text = String(value).trim();
  const dateOnly = DATE_ONLY.exec(text);
  if (dateOnly) return dateOnly[1];
  const instant = new Date(text);
  if (Number.isNaN(instant.getTime())) return null;
  return localTodayYmd(instant);
}

/** Overdue = still open and due on a day before today. No due date is never overdue. */
export function isTaskOverdue(
  value: string | null | undefined,
  status?: string | null,
  today: string = localTodayYmd()
): boolean {
  if (status === "completed" || status === "cancelled") return false;
  const day = taskDueDay(value);
  return !!day && day < today;
}

/**
 * Whole calendar days a task is past its due day: 0 when due today, later, or
 * never. Counted from the due DAY, not the instant, so a date-only follow-up
 * stored at midnight UTC is not read as the previous evening.
 */
export function taskDaysOverdue(value: string | null | undefined, today: string = localTodayYmd()): number {
  const day = taskDueDay(value);
  if (!day || day >= today) return 0;
  const toUtc = (ymd: string) => {
    const [y, m, d] = ymd.split("-").map(Number);
    return Date.UTC(y, m - 1, d);
  };
  return Math.round((toUtc(today) - toUtc(day)) / 86_400_000);
}

/**
 * The value for an `<input type="datetime-local">`: LOCAL wall-clock time.
 * `toISOString().slice(0, 16)` shows the UTC clock instead, four or five hours
 * off, and saving it back stored the typed time that far off.
 */
export function toDateTimeLocalInput(value: string | null | undefined): string {
  if (!value) return "";
  const day = taskDueDay(value);
  if (!day) return "";
  if (isDateOnlyValue(value)) return `${day}T00:00`;
  const d = new Date(String(value).trim());
  return `${day}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** The instant a datetime-local value means, as ISO, or null when empty/invalid. */
export function fromDateTimeLocalInput(local: string | null | undefined): string | null {
  if (!local) return null;
  // A zone-less "YYYY-MM-DDTHH:mm" is parsed as LOCAL time.
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** A datetime-local value N days from now, at the current local time. */
export function defaultDueInput(days: number, now: Date = new Date()): string {
  const d = new Date(now.getTime());
  d.setDate(d.getDate() + days);
  return `${localTodayYmd(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
